import React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import tailwindConfig from 'stylesheets/tailwind.config';

import ChevronDownIcon from '-!svg-react-loader?name=ChevronDownIcon!icons/chevron-down.svg';
import ChevronUpIcon from '-!svg-react-loader?name=ChevronUpIcon!icons/chevron-up.svg';
import SearchIcon from '-!svg-react-loader?name=SearchIcon!icons/search.svg';

import { resourceShape } from 'components/helpers/serialisableResources';

const { borderRadius } = tailwindConfig.theme;
const { borderWidth } = tailwindConfig.theme;
const { colors } = tailwindConfig.theme;
const { spacing } = tailwindConfig.theme;

export default function FolderSelect({
  createLabel,
  folders,
  isCreatable,
  isLoading,
  loadMoreRef,
  noOptionsMessage,
  onCreateOption,
  onInputChange,
  onMenuClose,
  onMenuOpen,
  onSelect,
}) {
  const options =
    folders?.map((folder) => ({
      label: folder.attributes.name,
      value: folder.id,
    })) || [];

  const SelectComponent = isCreatable ? CreatableSelect : Select;

  return (
    <div className='tw-relative tw-w-full'>
      <SearchIcon
        className='tw-absolute tw-z-[1] tw-ml-1 tw-mt-2 [&_path]:tw-fill-grey-700'
        height={24}
        width={24}
      />
      <SelectComponent
        blurInputOnSelect={false}
        closeMenuOnSelect={false}
        components={{
          DropdownIndicator,
          IndicatorSeparator: () => null,
          MenuList,
        }}
        filterOption={() => true}
        formatCreateLabel={createLabel}
        id='folder'
        isLoading={isLoading}
        loadMoreRef={loadMoreRef}
        name='folder'
        noOptionsMessage={() => noOptionsMessage || null}
        onChange={(option) => {
          if (isCreatable && option.__isNew__) {
            onCreateOption(option.value);
          } else {
            onSelect(option.value);
          }
        }}
        onInputChange={onInputChange}
        onMenuClose={onMenuClose}
        onMenuOpen={onMenuOpen}
        openMenuOnFocus={false}
        options={options}
        placeholder='Search for folder...'
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            borderColor: state.isFocused ? colors.blue[300] : colors.grey[300],
            boxShadow: 'none',
            height: spacing[10],
            minHeight: spacing[10],
            '&:hover': {
              borderColor:
                state.isFocused ? colors.blue[300] : colors.grey[400],
            },
          }),
          dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            padding: `${spacing[1.5]} ${spacing[1]}`,
          }),
          indicatorsContainer: (baseStyles) => ({
            ...baseStyles,
            height: spacing[9.5],
          }),
          input: (baseStyles) => ({
            ...baseStyles,
            margin: spacing[0],
            padding: spacing[0],
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            border: `${borderWidth[1]} solid ${colors.blue[100]}`,
            boxShadow: `${spacing[0]} ${spacing[0.5]} ${spacing[1]} ${spacing[0]} rgba(101, 116, 140, 0.12)`,
            zIndex: 10,
          }),
          menuList: (baseStyles) => ({
            ...baseStyles,
            borderRadius: borderRadius.lg,
            padding: spacing[0],
          }),
          noOptionsMessage: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: colors.grey['025'],
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            overflow: 'hidden',
            padding: spacing[3],
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }),
          placeholder: (baseStyles) => ({
            ...baseStyles,
            color: colors.grey[400],
            margin: spacing[0],
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
            height: spacing[9.5],
            padding: `${spacing[0]} ${spacing[2]} ${spacing[0]} ${spacing[8]}`,
            position: 'initial',
            width: '100%',
          }),
        }}
      />
    </div>
  );
}

function DropdownIndicator(props) {
  const Icon = props.selectProps.menuIsOpen ? ChevronUpIcon : ChevronDownIcon;

  return (
    <components.DropdownIndicator {...props}>
      <Icon className='[&_polygon]:tw-fill-grey-700' height={20} width={20} />
    </components.DropdownIndicator>
  );
}

function MenuList(props) {
  return (
    <components.MenuList {...props}>
      {props.children}
      {!!props.selectProps.loadMoreRef && !props.isLoading && (
        <div ref={props.selectProps.loadMoreRef} />
      )}
    </components.MenuList>
  );
}

FolderSelect.propTypes = {
  createLabel: PropTypes.func,
  folders: PropTypes.arrayOf(resourceShape('folder')),
  isCreatable: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadMoreRef: PropTypes.func,
  noOptionsMessage: PropTypes.node,
  onCreateOption: PropTypes.func,
  onInputChange: PropTypes.func.isRequired,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
};
